import { ReactNode, useId } from 'react'
import clsx from 'clsx'
import useTranslation from 'next-translate/useTranslation'

import HeadingText from '../HeadingText'
import BodyText from '../BodyText'

import styles from './WhatsNext.module.scss'

type WhatsNextSummaryRow = {
  label: string
  value: ReactNode
}

type WhatsNextProps = {
  /** What happens next, in order */
  steps: ReactNode[]
  /** Defaults to "What happens next" */
  title?: string
  icon?: ReactNode
  /** E.g. "Order placed" */
  headline?: ReactNode
  /** Key facts of what was just done, e.g. restaurant and time */
  summaryRows?: WhatsNextSummaryRow[]
  actions?: ReactNode
  className?: string
}

/**
 * Confirms what was just done and what happens next, so customers don't
 * have to wonder or call the restaurant
 */
export default function WhatsNext({
  steps,
  title,
  icon,
  headline,
  summaryRows,
  actions,
  className,
}: WhatsNextProps) {
  const { t } = useTranslation('common')
  const titleId = useId()

  return (
    <section className={clsx(styles.container, className)}>
      {(icon || headline) && (
        <div className={styles.header}>
          {icon && (
            <span className={styles.icon} aria-hidden>
              {icon}
            </span>
          )}
          {headline && <HeadingText as="h2">{headline}</HeadingText>}
        </div>
      )}
      {!!summaryRows?.length && (
        <dl className={styles.summary}>
          {summaryRows.map(({ label, value }) => (
            <div key={label} className={styles.summaryRow}>
              <dt className={styles.summaryLabel}>{label}</dt>
              <dd className={styles.summaryValue}>{value}</dd>
            </div>
          ))}
        </dl>
      )}
      <div className={styles.next} aria-labelledby={titleId} role="group">
        <HeadingText as="h3" id={titleId}>
          {title ?? t('whatsNext.title')}
        </HeadingText>
        <ol className={styles.steps}>
          {steps.map((step, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={index} className={styles.step}>
              <span className={styles.stepNumber} aria-hidden>
                {index + 1}
              </span>
              <BodyText as="span">{step}</BodyText>
            </li>
          ))}
        </ol>
      </div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </section>
  )
}
